/**
 * EL MENSUAL NUEVO EN LA TABLA, CON LOS COLORES DE CALMA (Enrique, 28-10).
 *
 * Los mensuales de Calma llegan a la tabla de Seguimiento con su respuesta corta y su letra
 * (`coloresDelReporte`). Los del formulario de la app del 25-10 se guardan en estrellas, y la
 * tabla los pintaba como «★★★☆☆» sobre gris: «quiero que los reportes nuevos tengan un estilo
 * similar a los que se importan de calma, con colores dependiendo de lo bueno o malo».
 *
 * Aquí cada respuesta de `respuestas_mensual` se traduce a una frase corta, con el vocabulario
 * de las cortas de Calma (`backend/core/letras_de_calma.py`), y a una letra:
 *
 *   5 y 4 estrellas → A (verde) · 3 → B (amarillo) · 2 → C (naranja) · 1 → D (rojo)
 *
 * En «¿Cuánto te ha costado?» la escala va al revés (1 es «Muy fácil»). Lo que dijo entero
 * («4 de 5 · de sus check-in») va en `larga`, que la tabla enseña al pasar el ratón.
 *
 * Puro: ni red ni pantalla.
 */

/** La fila de `respuestas_mensual` de esa pregunta del formulario, o `undefined`. */
const fila = (r, clave) => (Array.isArray(r?.respuestas_mensual) ? r.respuestas_mensual : [])
    .find((x) => x && x.clave === clave);

const LETRA_DEL_GRADO = { 5: 'A', 4: 'A', 3: 'B', 2: 'C', 1: 'D' };

const FRASES = {
    dieta_grado: { 5: 'Perfecta', 4: 'Casi perfecta', 3: 'Bastante bien, no del todo', 2: 'Regular', 1: 'Nada' },
    entreno_grado: { 5: 'Lo hice perfecto', 4: 'Lo hice prácticamente perfecto', 3: 'He fallado algunos días',
        2: 'He fallado bastante', 1: 'No hice nada' },
    cardio_grado: { 5: 'Las hice todas', 4: 'Casi todas', 3: 'Algunas, menos de las marcadas',
        2: 'Muy pocas', 1: 'Ninguna' },
    suplementacion_grado: { 5: 'Tomé todos', 4: 'Casi todos los días', 3: 'Me salté algunos días',
        2: 'Me salté bastantes días', 1: 'No tomé nada' },
    // Al revés: 1 es «Muy fácil» y 5 «Un infierno».
    dieta_coste: { 1: 'No costó nada', 2: 'Costó poco', 3: 'Costó, pero me adapto', 4: 'Costó bastante',
        5: 'Costó demasiado' },
};

const _estrellas = (f) => (f && Number.isInteger(f.valor) && f.valor >= 1 && f.valor <= 5 ? f.valor : null);

/** Una pregunta de estrellas: su frase, su letra y lo que dijo entero. */
const deEstrellas = (r, clave, { alReves = false } = {}) => {
    const f = fila(r, clave);
    const n = _estrellas(f);
    if (n == null) return null;
    return { valor: FRASES[clave][n], letra: LETRA_DEL_GRADO[alReves ? 6 - n : n], larga: f.texto || `${n} de 5` };
};

// Lo que pide para la dieta del mes que viene, en corto (`core/mensual.DIETA_PROXIMO`).
const DIETA_PROXIMO = { necesito_mas: 'súbeme macros', necesito_menos: 'bájame macros' };

/** «¿Te ha costado la dieta?»: lo que le costó y, si pide otros macros, cuáles. */
const costeDeLaDieta = (r) => {
    const coste = deEstrellas(r, 'dieta_coste', { alReves: true });
    const pide = DIETA_PROXIMO[fila(r, 'dieta_proximo')?.valor];
    if (!coste) {
        // Con una estrella en el cumplimiento no se le pregunta el coste: queda lo que pide.
        return pide ? { valor: `${pide[0].toUpperCase()}${pide.slice(1)}`, letra: 'C', larga: fila(r, 'dieta_proximo').texto } : null;
    }
    if (!pide) return coste;
    // Pedir otros macros ya es para mirarlo: como mínimo naranja, como en Calma.
    return { valor: `${coste.valor}, ${pide}`, letra: coste.letra === 'D' ? 'D' : 'C',
        larga: `${coste.larga} · ${fila(r, 'dieta_proximo').texto}` };
};

/** Las sesiones de cardio que contó la app (`cardio_del_mes`), en verde, amarillo, naranja o rojo. */
const cardioContado = (r) => {
    const c = r?.cardio_del_mes;
    if (!c || !c.previstas) return null;
    const pct = c.hechas / c.previstas;
    const letra = pct >= 0.9 ? 'A' : pct >= 0.7 ? 'B' : pct >= 0.4 ? 'C' : 'D';
    return { valor: `${c.hechas} de ${c.previstas} sesiones`, letra, larga: null };
};

/** «¿Has cumplido el cardio?»: lo que contestó; sin eso, lo que contó la app; sin eso, sus check-in. */
const cardio = (r) => {
    // Los del 25 al 28-10 lo contestaban en cuatro opciones (`cardio_cumplido`): esos, como estaban.
    if (fila(r, 'cardio_cumplido')) return null;
    const f = fila(r, 'cardio_grado');
    if (f && !f.de_checkin) return deEstrellas(r, 'cardio_grado');
    return cardioContado(r) || deEstrellas(r, 'cardio_grado');
};

/** Una de sí o no: el «no» es que todo va bien (verde); el «sí», lo que contó, en naranja. */
const deSiONo = (r, clave, siNo) => {
    const f = fila(r, clave);
    if (!f) return null;
    if (f.valor === 'no') return { valor: siNo, letra: 'A', larga: null };
    if (f.valor === 'si') return { valor: f.detalle || 'Sí', letra: 'C', larga: f.detalle || null };
    return null;
};

/** «¿Cambia tu objetivo?»: el mismo, en verde; el nuevo, en naranja, como en Calma. */
const objetivo = (r, nombreDelObjetivo) => {
    const f = fila(r, 'objetivo_cambio');
    if (!f) return null;
    if (f.valor === 'mismo') return { valor: 'Mismo objetivo (nada cambia)', letra: 'A', larga: null };
    if (f.valor === 'cambiar') {
        const nombre = r.proximo_objetivo ? (nombreDelObjetivo?.(r.proximo_objetivo) || r.proximo_objetivo) : null;
        return { valor: nombre ? `${nombre} (cambia)` : 'Quiere cambiar', letra: 'C', larga: null };
    }
    return null;
};

/**
 * La celda de un mensual del formulario de la app en la columna de esa pregunta (la clave de
 * `PREGUNTA_DEL_MENSUAL`), o `null` si ese reporte no la contestó así.
 * @param {object} reporte el de la ficha (con `respuestas_mensual`)
 * @param {string} pregunta `objetivo`, `cumplimientoDieta`, ...
 * @param {function} [nombreDelObjetivo] clave del objetivo → su nombre
 * @returns {{valor: string, letra: string, larga: string|null}|null}
 */
export const celdaDelMensualNuevo = (reporte, pregunta, nombreDelObjetivo) => {
    if (!Array.isArray(reporte?.respuestas_mensual) || !reporte.respuestas_mensual.length) return null;
    switch (pregunta) {
    case 'objetivo': return objetivo(reporte, nombreDelObjetivo);
    case 'cumplimientoDieta': return deEstrellas(reporte, 'dieta_grado');
    case 'esfuerzoParaCumplirDieta': return costeDeLaDieta(reporte);
    case 'tomaDeSuplementos': return deEstrellas(reporte, 'suplementacion_grado');
    case 'cumplimientoEntrenamiento': return deEstrellas(reporte, 'entreno_grado');
    case 'cumplimientoCardio': return cardio(reporte);
    case 'descanso': return deSiONo(reporte, 'descanso_resenable', 'Sin problema');
    case 'problemasParaEntrenar': return deSiONo(reporte, 'molestias', 'Ninguno');
    default: return null;
    }
};
